import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { Download, ExternalLink, FileText, FileType, ListChecks, Lightbulb, Link2, Search, X } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { STEPS, COMBINED } from "../data/steps";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OSF_URL = "https://osf.io/4g3a9/overview?view_only=5fc6436bfaf64eb6aad79385d740961a";

const FILTERS = [
  { value: "all", label: "All materials" },
  { value: "checklist", label: "Checklists" },
  { value: "template", label: "Templates" },
  { value: "tip-sheet", label: "Tip sheets" },
];

const KIND_ICON = { checklist: ListChecks, template: FileText, "tip-sheet": Lightbulb, resource: Link2 };

const TOTAL_MATERIALS = STEPS.reduce((a, s) => a + s.downloads.length, 0);

const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const slugify = (title) => norm(title).replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const baseName = (path) => (path ? path.split("/").pop() : "");
const fmtSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const trackDownload = (slug, title, format) => {
  try {
    fetch(`${API}/downloads/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ slug, title, format }),
      keepalive: true,
    });
  } catch {
    /* non-blocking */
  }
};

const Highlight = ({ text, query }) => {
  if (!query) return text;
  const lower = text.toLowerCase();
  const q = query.toLowerCase();
  const parts = [];
  let i = 0;
  while (true) {
    const j = lower.indexOf(q, i);
    if (j === -1) { parts.push(text.slice(i)); break; }
    if (j > i) parts.push(text.slice(i, j));
    parts.push(<mark key={j} className="rounded bg-melt-amber/40 px-0.5 text-melt-indigo">{text.slice(j, j + q.length)}</mark>);
    i = j + q.length;
  }
  return <>{parts}</>;
};

const FormatButton = ({ href, label, icon: Icon, size, onClick, testId }) => (
  <a
    href={href}
    download={baseName(href)}
    onClick={onClick}
    className="inline-flex items-center justify-center gap-2 rounded-md bg-melt-teal px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-melt-teal-600"
    data-testid={testId}
  >
    <Icon size={16} aria-hidden="true" />
    {label}
    {size && <span className="font-normal text-white/70">{size}</span>}
  </a>
);

const DownloadCard = ({ d, stepN, query, count, manifest }) => {
  const Icon = KIND_ICON[d.kind] || FileText;
  const slug = slugify(d.title);
  return (
    <div className="flex flex-col gap-4 rounded-lg border border-melt-grey bg-white p-5 sm:flex-row sm:items-center sm:justify-between" data-testid={`download-card-${stepN}-${slug}`}>
      <div className="flex items-start gap-4">
        <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-melt-teal/10 text-melt-teal">
          <Icon size={20} aria-hidden="true" />
        </span>
        <div>
          <p className="font-semibold text-melt-indigo"><Highlight text={d.title} query={query} /></p>
          <p className="mt-0.5 text-[15px] text-melt-indigo/70"><Highlight text={d.desc} query={query} /></p>
          {typeof count === "number" && (
            <p className="mt-1 text-xs font-medium uppercase tracking-wide text-melt-indigo/50">
              {count} download{count === 1 ? "" : "s"}
            </p>
          )}
        </div>
      </div>
      <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
        {d.word && (
          <FormatButton
            href={d.word}
            label="Word"
            icon={FileText}
            size={fmtSize(manifest[baseName(d.word)])}
            onClick={() => trackDownload(slug, d.title, "word")}
            testId={`download-word-${stepN}-${slug}`}
          />
        )}
        {d.pdf && (
          <FormatButton
            href={d.pdf}
            label="PDF"
            icon={FileType}
            size={fmtSize(manifest[baseName(d.pdf)])}
            onClick={() => trackDownload(slug, d.title, "pdf")}
            testId={`download-pdf-${stepN}-${slug}`}
          />
        )}
      </div>
    </div>
  );
};

export default function Toolkit() {
  const { hash } = useLocation();
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(["step-1"]);
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [counts, setCounts] = useState({});
  const [manifest, setManifest] = useState({});
  const firstRun = useRef(true);
  const searchRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 200);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    fetch(`${API}/downloads/counts`).then((r) => r.json()).then((d) => setCounts(d || {})).catch(() => {});
    fetch(`/downloads/manifest.json`).then((r) => r.json()).then((d) => setManifest(d || {})).catch(() => {});
  }, []);

  const q = norm(debounced);
  const perStep = useMemo(() => {
    return STEPS.map((s) => {
      const kindFiltered = s.downloads.filter((d) => filter === "all" || d.kind === filter);
      if (!q) return { step: s, items: kindFiltered };
      const stepMatch = norm(`${s.name} ${s.description}`).includes(q);
      const items = kindFiltered.filter((d) => stepMatch || norm(`${d.title} ${d.desc}`).includes(q));
      return { step: s, items };
    });
  }, [filter, q]);

  const shown = perStep.reduce((a, p) => a + p.items.length, 0);
  const noMatches = debounced && shown === 0;

  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      if (!debounced) return;
    }
    if (debounced) {
      setOpen(perStep.filter((p) => p.items.length > 0).map((p) => p.step.id));
    } else {
      setOpen(filter === "all" ? ["step-1"] : STEPS.map((s) => s.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced, filter]);

  useEffect(() => {
    const id = hash.replace("#", "");
    if (id && STEPS.some((s) => s.id === id)) {
      setOpen((prev) => (prev.includes(id) ? prev : [...prev, id]));
      const el = document.getElementById(id);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    } else if (hash === "#downloads") {
      const el = document.getElementById("downloads");
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
  }, [hash]);

  const clearSearch = () => {
    setQuery("");
    setDebounced("");
    if (searchRef.current) searchRef.current.focus();
  };

  return (
    <>
      <PageHeader
        eyebrow="The toolkit"
        title="The MELT toolkit"
        intro="MELT is organised as seven steps. Under each step you will find guidance materials such as checklists, templates, tip sheets, curated resources and links to related organisations. The materials use generic wording so that they can be adapted to any language pairing. Worked examples in English alongside a non English language illustrate the nuances that require attention during translation."
        testId="toolkit-header"
      />

      {/* Top controls */}
      <section id="downloads" className="scroll-mt-24 border-b border-melt-grey bg-white" aria-labelledby="controls-heading">
        <div className="mx-auto max-w-6xl px-5 py-10 md:px-8">
          <h2 id="controls-heading" className="sr-only">Toolkit search, downloads and filters</h2>
          <div className="rounded-xl border border-melt-grey woven-texture-light p-6 md:p-8">
            {/* Search — first and most prominent control */}
            <div>
              <label htmlFor="toolkit-search" className="text-sm font-semibold uppercase tracking-[0.16em] text-melt-indigo/70">
                Search
              </label>
              <div className="relative mt-3 w-full md:w-[400px]">
                <Search size={18} aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-melt-indigo/40" />
                <input
                  id="toolkit-search"
                  ref={searchRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search the toolkit, for example consent, translator, transcription"
                  className="w-full rounded-md border border-melt-grey bg-white py-3 pl-10 pr-10 text-base text-melt-indigo placeholder:text-melt-indigo/40 focus:border-melt-teal"
                  data-testid="toolkit-search-input"
                />
                {query && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    aria-label="Clear search"
                    className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-melt-indigo/60 hover:bg-melt-grey/60 hover:text-melt-indigo"
                    data-testid="toolkit-search-clear"
                  >
                    <X size={16} aria-hidden="true" />
                  </button>
                )}
              </div>
            </div>

            {/* Combined downloads */}
            <div className="mt-6 flex flex-col gap-4 border-t border-melt-grey pt-6 sm:flex-row sm:flex-wrap sm:items-center">
              <a
                href={COMBINED.pdf}
                download={COMBINED.pdf.split("/").pop()}
                onClick={() => trackDownload("entire-toolkit", "Entire toolkit", "pdf")}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-melt-teal px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-melt-teal-600"
                data-testid="download-all-pdf"
              >
                <FileType size={18} aria-hidden="true" />
                Download all as PDF
                <span className="font-normal text-white/70">{fmtSize(manifest[baseName(COMBINED.pdf)])}</span>
              </a>
              <a
                href={COMBINED.wordZip}
                download={COMBINED.wordZip.split("/").pop()}
                onClick={() => trackDownload("entire-toolkit", "Entire toolkit", "word")}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-melt-teal px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-melt-teal-600"
                data-testid="download-all-word"
              >
                <FileText size={18} aria-hidden="true" />
                Download all as Word
                <span className="font-normal text-white/70">{fmtSize(manifest[baseName(COMBINED.wordZip)])}</span>
              </a>
              <a
                href={OSF_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-melt-indigo/25 bg-white px-6 py-3 text-base font-semibold text-melt-indigo transition-colors hover:border-melt-teal hover:text-melt-teal"
                data-testid="view-on-osf"
              >
                <ExternalLink size={18} aria-hidden="true" />
                View on the Open Science Framework
                <span className="sr-only">(opens in a new tab)</span>
              </a>
            </div>

            {/* Filter control */}
            <div className="mt-6 border-t border-melt-grey pt-6">
              <p id="filter-label" className="text-sm font-semibold uppercase tracking-[0.16em] text-melt-indigo/70">Show</p>
              <div className="mt-3 flex flex-wrap gap-2" role="group" aria-labelledby="filter-label" data-testid="material-filter">
                {FILTERS.map((f) => {
                  const active = filter === f.value;
                  return (
                    <button
                      key={f.value}
                      type="button"
                      onClick={() => setFilter(f.value)}
                      aria-pressed={active}
                      className={[
                        "rounded-full border px-4 py-2 text-sm font-semibold transition-colors",
                        active ? "border-melt-teal bg-melt-teal text-white" : "border-melt-grey bg-white text-melt-indigo hover:border-melt-teal hover:text-melt-teal",
                      ].join(" ")}
                      data-testid={`filter-${f.value}`}
                    >
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Step sections */}
      <section className="mx-auto max-w-5xl px-5 py-12 md:px-8 md:py-16" aria-label="Toolkit steps">
        <p className="mb-8 rounded-lg border border-melt-grey bg-white px-5 py-4 text-[15px] text-melt-indigo/80" data-testid="format-rationale">
          Every material is available as an editable Word file and as a PDF. Use the Word version if you
          intend to adapt the template for your own project.
        </p>

        {/* Live result count */}
        <p role="status" aria-live="polite" className="mb-6 text-[15px] font-medium text-melt-indigo/75" data-testid="results-count">
          Showing {shown} of {TOTAL_MATERIALS} materials
        </p>

        {noMatches && (
          <div className="mb-8 rounded-lg border border-melt-grey bg-white p-8" data-testid="no-results">
            <p className="text-base text-melt-indigo/80">
              No materials match that term. Try a broader keyword, or browse the seven steps below.
            </p>
            <button
              type="button"
              onClick={clearSearch}
              className="mt-4 inline-flex items-center gap-2 rounded-md bg-melt-teal px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-melt-teal-600"
              data-testid="no-results-clear"
            >
              <X size={16} aria-hidden="true" />
              Clear search
            </button>
          </div>
        )}

        <Accordion type="multiple" value={open} onValueChange={setOpen} className="space-y-4" data-testid="toolkit-accordion">
          {perStep.map(({ step: s, items }) => (
            <div id={s.id} key={s.id} className="scroll-mt-24">
              <AccordionItem value={s.id} className="overflow-hidden rounded-lg border border-melt-grey bg-white !border-b" data-testid={`toolkit-${s.id}`}>
                <AccordionTrigger className="gap-4 px-6 py-5 text-left hover:no-underline">
                  <span className="flex items-center gap-5">
                    <span className="font-serif text-3xl font-semibold leading-none text-melt-amber">{s.n}</span>
                    <span className="font-serif text-lg font-semibold text-melt-indigo md:text-xl">
                      <Highlight text={s.name} query={debounced} />
                    </span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-6">
                  <p className="max-w-3xl text-base leading-relaxed text-melt-indigo/80">
                    <Highlight text={s.description} query={debounced} />
                  </p>
                  <div className="mt-6 space-y-3">
                    {items.length > 0 ? (
                      items.map((d) => (
                        <DownloadCard key={d.title} d={d} stepN={s.n} query={debounced} count={counts[slugify(d.title)]} manifest={manifest} />
                      ))
                    ) : (
                      <p className="rounded-md border border-dashed border-melt-grey bg-melt-off-white px-4 py-4 text-[15px] text-melt-indigo/60" data-testid={`no-materials-${s.id}`}>
                        {debounced ? "No materials in this step match your search." : "No materials of the selected type in this step."}
                      </p>
                    )}
                  </div>
                  {s.examples && filter === "all" && !debounced && (
                    <div className="mt-8 border-t border-melt-grey pt-6" data-testid={`examples-${s.id}`}>
                      <h3 className="font-serif text-lg font-semibold text-melt-indigo">{s.examples.heading}</h3>
                      <p className="mt-2 max-w-3xl text-[15px] text-melt-indigo/70">{s.examples.note}</p>
                      <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {s.examples.images.map((img, idx) => (
                          <div key={idx} className="overflow-hidden rounded-lg border border-melt-grey bg-white" data-testid={`example-flyer-${s.n}-${idx}`}>
                            <a href={img.src} target="_blank" rel="noopener noreferrer" className="group block overflow-hidden" aria-label={`View full size: ${img.alt}`}>
                              <img src={img.src} alt={img.alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
                            </a>
                            <a
                              href={img.src}
                              download={img.src.split("/").pop()}
                              onClick={() => trackDownload(`recruitment-flyer-${idx + 1}`, `Recruitment flyer ${idx + 1}`)}
                              className="flex items-center justify-center gap-2 border-t border-melt-grey py-2.5 text-sm font-semibold text-melt-teal transition-colors hover:bg-melt-teal/5"
                              data-testid={`example-flyer-download-${s.n}-${idx}`}
                            >
                              <Download size={15} aria-hidden="true" />
                              Download flyer {idx + 1}
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            </div>
          ))}
        </Accordion>
      </section>

      {/* Licence & version panel */}
      <section className="woven-texture-light border-t border-melt-grey" aria-labelledby="licence-heading">
        <div className="mx-auto max-w-5xl px-5 py-12 md:px-8">
          <div className="rounded-lg border border-melt-grey bg-white p-8" data-testid="licence-panel">
            <h2 id="licence-heading" className="font-serif text-xl font-semibold">Version and licence</h2>
            <dl className="mt-5 grid gap-6 sm:grid-cols-3">
              <div>
                <dt className="text-sm font-semibold uppercase tracking-wide text-melt-indigo/50">Version</dt>
                <dd className="mt-1 text-base text-melt-indigo">1.0</dd>
              </div>
              <div>
                <dt className="text-sm font-semibold uppercase tracking-wide text-melt-indigo/50">Current version date</dt>
                <dd className="mt-1 text-base text-melt-indigo">June 2026</dd>
              </div>
              <div>
                <dt className="text-sm font-semibold uppercase tracking-wide text-melt-indigo/50">Reuse licence</dt>
                <dd className="mt-1 text-base text-melt-indigo">Creative Commons Attribution-NonCommercial 4.0 (CC BY-NC 4.0)</dd>
              </div>
            </dl>
            <p className="mt-6 border-t border-melt-grey pt-5 text-base text-melt-indigo/80">
              MELT is a living resource. Please submit feedback through the{" "}
              <Link to="/contact" className="link-underline font-medium">Contact us page</Link>{" "}
              so that the toolkit can continue to develop.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
